(ns app.front.view.party.registered-contractor
  (:require [app.domain.contractor-registration.model :as cr-model]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.party.common :as common]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub ::data :-> ::data)

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ trade-name]]
    (let [request-id (random-uuid)]
      {:comm/command {:command    :contractor-registration/trades
                      :method     :get
                      :data       {:workspace-id (get-in db [:view/workspace :workspace/id])}
                      :on-success [::init.success request-id trade-name]}
       :db           (assoc db ::data {::request-id request-id})})))

(rf/reg-event-db ::clear
  (fn [db _]
    (dissoc db ::data)))

(rf/reg-event-fx ::init.success
  (fn [{:keys [db]} [_ request-id trade-name _ results]]
    (when (= request-id (get-in db [::data ::request-id]))
      {:dispatch [::do-search]
       :db       (-> db
                     (assoc-in [::data ::cr-work-type-names] (:cr-work-type-names results))
                     (assoc-in [::data ::trade-filter] trade-name))})))


(rf/reg-sub ::trade-filter
  :<- [::data]
  :-> ::trade-filter)


(rf/reg-sub ::search-term
  :<- [::data]
  :-> ::search-term)


(rf/reg-sub ::search-in-progress?
  :<- [::data]
  :-> ::search-in-progress?)


(rf/reg-sub ::search-results
  :<- [::data]
  :-> ::search-results)


(rf/reg-sub ::cr-work-type-names
  :<- [::data]
  :-> ::cr-work-type-names)


(rf/reg-event-db ::do-search.success
  (fn [db [_ request-id _ results]]
    (if (= request-id (get-in db [::data ::search-request-id]))
      ;; filter only registrations with registrant user/business
      (assoc-in db [::data ::search-results] (filter :registrant (:results results)))
      db)))


(rf/reg-event-db ::do-search.done
  (fn [db [_ request-id]]
    (if (= request-id (get-in db [::data ::search-request-id]))
      (assoc-in db [::data ::search-in-progress?] false)
      db)))

(defn invalidate-search [db]
  (update db ::data dissoc ::search-request-id ::search-in-progress?))

(defn update-term [db term]
  (if (str/blank? term)
    (update db ::data dissoc ::search-term ::search-results ::search-in-progress?)
    (assoc-in db [::data ::search-term] term)))

(defn forget-registrant [db]
  (update db ::common/document dissoc :party/registration))

(rf/reg-event-fx ::search-text-change
  (fn [{:keys [db]} [_ term]]
    {:dispatch-debounce [{:id      ::do-search
                          :timeout 500
                          :action  :dispatch
                          :event   [::do-search]}]
     :db                (-> db
                            forget-registrant
                            (update-term term)
                            invalidate-search)}))


(rf/reg-event-fx ::do-search
  (fn [{:keys [db]} _]
    (let [ws-id      (get-in db [:view/workspace :workspace/id])
          term       (get-in db [::data ::search-term])
          trade      (get-in db [::data ::trade-filter])
          request-id (random-uuid)]
      (when (or term trade)
        {:comm/command {:command          :contractor-registration/find
                        :data             (cond-> {:workspace-id ws-id}
                                            true (assoc :conditions [{:filter.condition/attribute :domain/state
                                                                      :filter.condition/operator  :equals
                                                                      :filter.condition/values    #{:contractor-registration.state/active}}])
                                            term (assoc :search-term term)
                                            trade (update :conditions concat [{:filter.condition/attribute :work-type-names
                                                                               :filter.condition/operator  :equals
                                                                               :filter.condition/values    #{trade}}]))
                        :on-success       [::do-search.success request-id]
                        :finally-dispatch [::do-search.done request-id]}
         :db           (-> db
                           (assoc-in [::data ::search-request-id] request-id)
                           (assoc-in [::data ::search-in-progress?] true))}))))

(defn set-filter [db trade]
  (if (str/blank? trade)
    (update db ::data dissoc ::trade-filter)
    (assoc-in db [::data ::trade-filter] trade)))

(rf/reg-event-fx ::set-trade-filter
  (fn [{:keys [db]} [_ trade]]
    {:dispatch-debounce [{:id      ::do-search
                          :timeout 500
                          :action  :dispatch
                          :event   [::do-search]}]
     :db                (-> db
                            forget-registrant
                            (set-filter trade)
                            invalidate-search)}))


(rf/reg-event-fx ::add-existing-registration.success
  (fn [{:keys [db]} [_ on-success]]
    {:db         (common/reset-db db)
     :dispatch-n (cond-> [[::common/close]]
                   on-success (conj on-success))}))

(defn selected-roles [document]
  (or (some-> document ::common/cr-party-role hash-set)
      (-> document :party/party-roles set)))

(rf/reg-event-fx ::add-existing-registration
  (fn [{:keys [db]} [_ target opts]]
    (let [registration (-> db ::common/document :party/registration)
          roles        (-> db ::common/document selected-roles)]
      {:dispatch [::process/signal
                  target
                  [:process.signal/add-existing-contractor
                   {:contractor-registration/id (:contractor-registration/id registration)
                    :party/party-roles          roles}]
                  {:notify      {:success :changes-saved}
                   :on-success  [::add-existing-registration.success (:on-success opts)]
                   :track-save? true}]})))

(rf/reg-sub ::role-selected?
  :<- [::common/document]
  :-> (comp some? seq selected-roles))

(rf/reg-sub ::show-role-selector?
  :<- [::common/document]
  (fn [{::common/keys [cr-party-role]} _]
    ;; role selector is not shown if the dialog was opened by clicking on "add contractor from registry" from
    ;; a role requirement button
    (nil? cr-party-role)))

(defn registration-display-name [lang registration]
  [:span
   (str (i18n-model/get-text-from-entities-or-map (:work-type-names registration) lang) " || " (cr-model/format-name registration))])

(defn add-contractor-buttons [{:keys [tr target]}]
  (let [registrant     (<sub [::common/party-registration])
        role-selected? (<sub [::role-selected?])]
    [:div
     [ui-action-buttons/action-buttons {:buttons (remove nil?
                                                         [{:icon     i/remove
                                                           :text     (tr [:party/cancel-button])
                                                           :on-click #(do (>evt [::common/close])
                                                                          (>evt [::clear]))}
                                                          (when (and registrant role-selected?)
                                                            {:icon     i/user-plus
                                                             :on-click #(>evt [::add-existing-registration target])
                                                             :text     (tr [:party/add-contractor])})])}]]))

(defn contractor-search [tr target & [{:keys [hide-role-selector? on-select]}]]
  (let [target-id           (:db/id target)
        lang                (<sub [:language])
        work-type-names     (<sub [::cr-work-type-names])
        selected-registrant (<sub [::common/party-registration])
        validator           (<sub [::common/validator])]
    [:div.contractor-search.relative.w-full.my-6
     [:div.flex
      [:div.flex-1.mr-2
       [:div.leading-6 (tr [:party/search-for-a-contractor])]
       [ui/search {:id                      "party-contractor-search"
                   :container-class         "mt-3"
                   :placeholder             (tr [:party-modal/business-search-placeholder]
                                                [(tr [:business/id])])
                   :close-button-label-text (tr [:search/empty])
                   :value                   (<sub [::search-term])
                   :on-change               #(rf/dispatch-sync [::search-text-change (-> % futil/tval)])
                   :search-in-progress?     (<sub [::search-in-progress?])
                   :loading-style           {:top "3px"}
                   :loading-text            (tr [:search/loading])
                   :clear-fn                #(>evt [::search-text-change ""])}]]
      [:div.flex-1
       [:div.mb-2.leading-6 (tr [:party/filter-by-trade])]
       [ui-select/select {:id           "filter-by-trade"
                          :class-name   "full-width-select py-1"
                          :select-text  (tr [:party/select-trade])
                          :label-text   ""
                          :hide-label   true
                          :value        (<sub [::trade-filter])
                          :on-change    (fn [trade]
                                          (>evt [::set-trade-filter trade]))
                          :val->str     identity
                          :allow-empty? true
                          :values       work-type-names
                          :disabled     false}]]]
     (when-let [results (<sub [::search-results target-id])]
       [:div.bg-gray-200.my-4.flex-row.rounded-md
        (if (empty? results)
          [:div.p-2 (tr [:party-modal/no-contractors-found])]
          [:ul.p-2
           (for-frag [registration results]
             (let [add-permitted? (<sub [::process/signal-permitted?
                                         target-id
                                         [:process.signal/add-existing-contractor
                                          {:contractor-registration/id (:contractor-registration/id registration)}]])]
               [:li.p-4.rounded-md
                (merge {:class [(when (= selected-registrant registration) "bg-ui-night-sky-90 text-white")
                                (if add-permitted?
                                  "cursor-pointer hover:bg-ui-night-sky-90 hover:text-white"
                                  "opacity-50")]
                        :role  "button"}
                       (when add-permitted?
                         {:on-click #(if on-select
                                       (on-select registration)
                                       (>evt [::common/set-doc-value [:party/registration] registration]))}))
                [registration-display-name lang registration]]))])])
     (when (and (not hide-role-selector?)
                (<sub [::show-role-selector?]))
       [:div.mt-4
        [common/role-selector (util/mmap tr target-id validator)]])]))
